/* eslint-disable max-lines-per-function */
import { useArbeitszeitDelete } from '@/domain/arbeitszeiten/hooks';
import { Konflikt } from '@/domain/arbeitszeiten/components/Table/ToDoTable/entities/Konflikt';
import { useIsMonatAbgeschlossen } from '@/domain/arbeitszeiten/hooks/useIsMonatAbgeschlossen';
import { useProfile } from '@/hooks';
import { handleOnCompletedDelete } from '@/domain/arbeitszeiten/components/EditSheet/handleOnCompletedDelete';
import { toast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { useState } from 'react';
import { useAbwesenheitDelete } from '@/domain/arbeitszeiten/hooks/useAbwesenheitDelete';
import { ActionCellContentKonflikt } from '@/domain/arbeitszeiten/components/Table/ToDoTable/cells/ActionCell';
import { KonfliktWrapper } from '@/domain/arbeitszeiten/components/Table/ToDoTable/cells/KonfliktWrapper';
import { Abwesenheit } from '@/domain/arbeitszeiten/components/Table/ToDoTable/entities/Abwesenheit';
import { Arbeitszeit } from '@/domain/arbeitszeiten/components/Table/ToDoTable/entities/Arbeitszeit';
import { useStandortContext } from '@/features/standortContext/StandortContext';
import { useParams } from 'react-router-dom';

export function KonfliktActionWrapper({ konflikt }: { konflikt: Konflikt }) {
  const [arbeitszeitDelete] = useArbeitszeitDelete();
  const [abwesenheitDelete] = useAbwesenheitDelete();
  const { currentStandort } = useStandortContext();
  const { year, month, day } = useParams();
  const { t } = useTranslation();
  const [isDisabled, setIsDisabled] = useState(false);
  const { manager } = useProfile();

  const isMonatAbgeschlossen = useIsMonatAbgeschlossen(
    `${year}-${month}-${day}`,
    currentStandort.id,
  );

  function handleDelete(arbeitszeitId: string) {
    return async () => {
      setIsDisabled(true);
      try {
        const result = await arbeitszeitDelete({
          arbeitszeitId: arbeitszeitId,
        });
        handleOnCompletedDelete(result.data, t);
      } catch (e) {
        toast({
          title: t(
            'domain.arbeitszeiten.sheet.header.arbeitszeitDeleteUnknownError',
          ),
        });
        Sentry.captureException(e);
      } finally {
        setIsDisabled(false);
      }
    };
  }

  function handleDeleteAbweseneheit(abwesenheitId: string) {
    // eslint-disable-next-line complexity
    return async () => {
      setIsDisabled(true);
      try {
        const result = await abwesenheitDelete({ id: abwesenheitId });
        if (result.error) {
          toast({
            title: t(
              'domain.arbeitszeiten.abwesenheiten.delete.genericError.title',
            ),
            description: t(
              'domain.arbeitszeiten.abwesenheiten.delete.genericError.description',
            ),
          });
          return;
        }
        if (result.data.arbeitszeiten_abwesenheiten_delete.error) {
          Sentry.captureException(
            new Error(
              result.data.arbeitszeiten_abwesenheiten_delete.error.message,
            ),
          );

          if (
            result.data.arbeitszeiten_abwesenheiten_delete.error.__typename ===
            'Arbeitszeiten_AbwesenenheitNotFound'
          ) {
            toast({
              title: t(
                'domain.arbeitszeiten.abwesenheiten.delete.notFound.title',
              ),
              description: t(
                'domain.arbeitszeiten.abwesenheiten.delete.notFound.description',
              ),
            });
            return;
          }
          if (
            result.data.arbeitszeiten_abwesenheiten_delete.error.__typename ===
            'Arbeitszeiten_MonatAbgeschlossen'
          ) {
            toast({
              title: t(
                'domain.arbeitszeiten.abwesenheiten.delete.monatAbgeschlossen.title',
              ),
              description: t(
                'domain.arbeitszeiten.abwesenheiten.delete.monatAbgeschlossen.description',
              ),
            });
            return;
          }
          if (
            result.data.arbeitszeiten_abwesenheiten_delete.error.__typename ===
            'Arbeitszeiten_GenericError'
          ) {
            toast({
              title: t(
                'domain.arbeitszeiten.abwesenheiten.delete.genericError.title',
              ),
              description: t(
                'domain.arbeitszeiten.abwesenheiten.delete.genericError.description',
              ),
            });
            return;
          }
        }
        toast({
          title: t('domain.arbeitszeiten.abwesenheiten.delete.success.title'),
          description: t(
            'domain.arbeitszeiten.abwesenheiten.delete.success.description',
          ),
        });
      } catch (error) {
        Sentry.captureException(error);
        toast({
          title: t(
            'domain.arbeitszeiten.abwesenheiten.delete.genericError.title',
          ),
          description: t(
            'domain.arbeitszeiten.abwesenheiten.delete.genericError.description',
          ),
        });
      } finally {
        setIsDisabled(false);
      }
    };
  }

  return (
    <KonfliktWrapper
      borderCause={false}
      borderRoot={false}
      root={
        <ActionCellContentKonflikt
          arbeitszeitId={konflikt.rootArbeitszeit.id}
          onDelete={handleDelete(konflikt.rootArbeitszeit.id)}
          disabled={isMonatAbgeschlossen || isDisabled}
          deleteInfoText={t(
            'domain.arbeitszeiten.sheet.arbeitszeit.confirmDelete',
          )}
        />
      }
      cause={
        konflikt.conflictCause === null ? (
          ''
        ) : (
          <>
            {konflikt.conflictCause instanceof Abwesenheit && (
              <ActionCellContentKonflikt
                disabled={!manager.rechte.kalender}
                showEdit={false}
                onDelete={handleDeleteAbweseneheit(konflikt.conflictCause.id)}
                deleteInfoText={t(
                  'domain.arbeitszeiten.abwesenheiten.delete.confirmDelete',
                )}
              />
            )}
            {konflikt.conflictCause instanceof Arbeitszeit && (
              <ActionCellContentKonflikt
                disabled={isMonatAbgeschlossen || isDisabled}
                onDelete={handleDelete(konflikt.conflictCause.id)}
                arbeitszeitId={konflikt.conflictCause.id}
                deleteInfoText={t(
                  'domain.arbeitszeiten.sheet.arbeitszeit.confirmDelete',
                )}
              />
            )}
          </>
        )
      }
    />
  );
}
